import React from "react";
import {
  Alert,
  Box,
  Button,
  CardContent,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AccountBalanceRoundedIcon from "@mui/icons-material/AccountBalanceRounded";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import AssessmentRoundedIcon from "@mui/icons-material/AssessmentRounded";
import BalanceRoundedIcon from "@mui/icons-material/BalanceRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import PointOfSaleRoundedIcon from "@mui/icons-material/PointOfSaleRounded";
import QueryStatsRoundedIcon from "@mui/icons-material/QueryStatsRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SavingsRoundedIcon from "@mui/icons-material/SavingsRounded";
import ShoppingCartCheckoutRoundedIcon from "@mui/icons-material/ShoppingCartCheckoutRounded";
import ShowChartRoundedIcon from "@mui/icons-material/ShowChartRounded";
import TrendingDownRoundedIcon from "@mui/icons-material/TrendingDownRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import { useNavigate, useParams } from "react-router-dom";
import { erpApi } from "../../api/erp";
import { projectsApi } from "../../api/projects";
import ModuleDisabledState from "../../components/dashboard/ModuleDisabledState";
import {
  ErpEmpty,
  ErpLoading,
  ErpMetricCard,
  ErpPanel,
  MovementStatusChip,
} from "../../components/dashboard/erp/ErpUi";
import { useProjects } from "../../context/ProjectsContext";
import { isProjectModuleEnabled } from "../../utils/projectModules";
import {
  ACCOUNT_TYPE_LABELS,
  ERP_BRAND,
  MOVEMENT_KIND_LABELS,
  formatDateTime,
  formatMoney,
  toDateInput,
} from "../../utils/erp";

const PROFIT = "#159455";
const LOSS = "#C83A3A";
const COST = "#F08A3E";
const BLUE = "#2F80ED";
const PURPLE = "#7657D5";

function startOfMonth() {
  const date = new Date();
  return toDateInput(new Date(date.getFullYear(), date.getMonth(), 1));
}

function rangeForPreset(preset) {
  const now = new Date();
  const today = toDateInput(now);
  if (preset === "today") return { preset, dateFrom: today, dateTo: today };
  if (preset === "week") {
    const from = new Date(now);
    from.setDate(now.getDate() - 6);
    return { preset, dateFrom: toDateInput(from), dateTo: today };
  }
  if (preset === "30days") {
    const from = new Date(now);
    from.setDate(now.getDate() - 29);
    return { preset, dateFrom: toDateInput(from), dateTo: today };
  }
  return { preset: "month", dateFrom: startOfMonth(), dateTo: today };
}

function safeNumber(value) {
  const number = Number(value || 0);
  return Number.isFinite(number) ? number : 0;
}

function previousRangeFor(dateFrom, dateTo) {
  const from = new Date(`${dateFrom}T12:00:00`);
  const to = new Date(`${dateTo}T12:00:00`);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return null;
  const dayMs = 24 * 60 * 60 * 1000;
  const durationDays = Math.max(1, Math.round((to.getTime() - from.getTime()) / dayMs) + 1);
  const previousTo = new Date(from.getTime() - dayMs);
  const previousFrom = new Date(previousTo.getTime() - ((durationDays - 1) * dayMs));
  return { dateFrom: toDateInput(previousFrom), dateTo: toDateInput(previousTo) };
}

function comparisonMetric(current, previous) {
  const currentValue = safeNumber(current);
  const previousValue = safeNumber(previous);
  return {
    current: currentValue,
    previous: previousValue,
    difference: currentValue - previousValue,
    percent: Math.abs(previousValue) < 0.005 ? null : ((currentValue - previousValue) / Math.abs(previousValue)) * 100,
  };
}

function formatPercent(value, decimals = 1) {
  return `${safeNumber(value).toLocaleString("es-AR", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}%`;
}

function movementTotals(movements = {}, kinds = []) {
  return kinds.reduce((total, kind) => total + safeNumber(movements?.[kind]?.total), 0);
}

function comparisonMeta(metric, { inverse = false } = {}) {
  if (!metric || metric.percent === null || metric.percent === undefined) {
    return {
      label: "Todavía no hay un período anterior comparable",
      direction: "flat",
      favorable: null,
    };
  }
  const percent = safeNumber(metric.percent);
  return {
    label: `${Math.abs(percent).toLocaleString("es-AR", { maximumFractionDigits: 1 })}% frente al período anterior`,
    direction: percent > 0 ? "up" : percent < 0 ? "down" : "flat",
    favorable: inverse ? percent <= 0 : percent >= 0,
  };
}

function TrendChip({ metric, inverse = false }) {
  const meta = comparisonMeta(metric, { inverse });
  const icon = meta.direction === "up"
    ? <ArrowUpwardRoundedIcon />
    : meta.direction === "down"
      ? <ArrowDownwardRoundedIcon />
      : <ShowChartRoundedIcon />;
  return (
    <Chip
      size="small"
      icon={icon}
      label={meta.label}
      sx={{
        height: "auto",
        maxWidth: "100%",
        fontWeight: 850,
        bgcolor: meta.favorable === null
          ? "rgba(25,36,47,0.07)"
          : meta.favorable
            ? "rgba(21,148,85,0.11)"
            : "rgba(200,58,58,0.1)",
        color: meta.favorable === null ? ERP_BRAND.muted : meta.favorable ? PROFIT : LOSS,
        "& .MuiChip-icon": { color: "inherit", fontSize: 17 },
        "& .MuiChip-label": {
          py: 0.55,
          whiteSpace: "normal",
          lineHeight: 1.25,
        },
      }}
    />
  );
}

function SimpleMetric({ icon, label, value, description, tone, trend, inverseTrend = false }) {
  const tones = {
    green: { color: PROFIT, bg: "rgba(21,148,85,0.1)" },
    orange: { color: COST, bg: "rgba(240,138,62,0.11)" },
    blue: { color: BLUE, bg: "rgba(47,128,237,0.1)" },
    purple: { color: PURPLE, bg: "rgba(118,87,213,0.1)" },
    red: { color: LOSS, bg: "rgba(200,58,58,0.1)" },
    dark: { color: ERP_BRAND.dark, bg: "rgba(25,36,47,0.08)" },
  };
  const selected = tones[tone] || tones.blue;
  return (
    <ErpPanel sx={{ height: "100%", transition: "transform .18s ease, box-shadow .18s ease", "&:hover": { transform: "translateY(-2px)", boxShadow: "0 16px 34px rgba(25,36,47,0.09)" } }}>
      <CardContent sx={{ p: { xs: 2, sm: 2.5 }, "&:last-child": { pb: { xs: 2, sm: 2.5 } } }}>
        <Stack direction="row" justifyContent="space-between" spacing={{ xs: 1.25, sm: 2 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" sx={{ color: ERP_BRAND.muted, fontWeight: 900, letterSpacing: 0.45 }}>{label}</Typography>
            <Typography variant="h5" sx={{ color: ERP_BRAND.dark, fontWeight: 950, mt: 0.7, fontSize: { xs: 22, sm: 24 }, lineHeight: 1.15, overflowWrap: "anywhere" }}>{value}</Typography>
            <Typography variant="body2" sx={{ color: ERP_BRAND.muted, mt: 0.65 }}>{description}</Typography>
          </Box>
          <Box sx={{ width: { xs: 40, sm: 46 }, height: { xs: 40, sm: 46 }, borderRadius: 3, display: "grid", placeItems: "center", bgcolor: selected.bg, color: selected.color, flexShrink: 0 }}>
            {icon}
          </Box>
        </Stack>
        {trend ? <Box sx={{ mt: 1.5 }}><TrendChip metric={trend} inverse={inverseTrend} /></Box> : null}
      </CardContent>
    </ErpPanel>
  );
}

function InsightCard({ icon, title, text, tone = "info" }) {
  const tones = {
    success: { color: PROFIT, bg: "rgba(21,148,85,0.09)", border: "rgba(21,148,85,0.2)" },
    warning: { color: COST, bg: "rgba(240,138,62,0.09)", border: "rgba(240,138,62,0.22)" },
    error: { color: LOSS, bg: "rgba(200,58,58,0.08)", border: "rgba(200,58,58,0.2)" },
    info: { color: BLUE, bg: "rgba(47,128,237,0.08)", border: "rgba(47,128,237,0.2)" },
  };
  const selected = tones[tone] || tones.info;
  return (
    <Box sx={{ p: { xs: 1.6, sm: 2 }, borderRadius: 3, bgcolor: selected.bg, border: `1px solid ${selected.border}`, height: "100%" }}>
      <Stack direction="row" spacing={1.3} alignItems="flex-start">
        <Box sx={{ color: selected.color, mt: 0.15, display: "flex" }}>{icon}</Box>
        <Box>
          <Typography sx={{ fontWeight: 950, color: ERP_BRAND.dark }}>{title}</Typography>
          <Typography variant="body2" sx={{ color: ERP_BRAND.muted, mt: 0.35, lineHeight: 1.55 }}>{text}</Typography>
        </Box>
      </Stack>
    </Box>
  );
}

function TrendChart({ data = [], currency = "ARS" }) {
  if (!data.length) {
    return (
      <ErpEmpty
        title="La tendencia se está construyendo"
        description="Cuando haya ingresos y egresos contabilizados en distintos días, acá vas a ver cómo evoluciona el negocio."
      />
    );
  }

  const width = 760;
  const height = 250;
  const left = 20;
  const right = 18;
  const top = 22;
  const bottom = 45;
  const chartWidth = width - left - right;
  const chartHeight = height - top - bottom;
  const maxValue = Math.max(1, ...data.flatMap((point) => [Math.abs(safeNumber(point.income)), Math.abs(safeNumber(point.expenses))]));
  const x = (index) => left + (data.length === 1 ? chartWidth / 2 : (index / (data.length - 1)) * chartWidth);
  const y = (value) => top + chartHeight - (Math.max(0, safeNumber(value)) / maxValue) * chartHeight;
  const incomePoints = data.map((point, index) => `${x(index)},${y(point.income)}`).join(" ");
  const expensePoints = data.map((point, index) => `${x(index)},${y(point.expenses)}`).join(" ");
  const labelIndexes = [...new Set([0, Math.floor((data.length - 1) / 2), data.length - 1])];

  return (
    <Box>
      <Stack direction="row" spacing={2.2} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        <Stack direction="row" spacing={0.8} alignItems="center"><Box sx={{ width: 10, height: 10, borderRadius: 99, bgcolor: PROFIT }} /><Typography variant="caption" sx={{ fontWeight: 850 }}>Ingresos</Typography></Stack>
        <Stack direction="row" spacing={0.8} alignItems="center"><Box sx={{ width: 10, height: 10, borderRadius: 99, bgcolor: COST }} /><Typography variant="caption" sx={{ fontWeight: 850 }}>Egresos</Typography></Stack>
      </Stack>
      <Box sx={{ width: "100%", overflowX: { xs: "hidden", sm: "auto" } }}>
        <Box component="svg" viewBox={`0 0 ${width} ${height}`} sx={{ width: "100%", minWidth: { xs: 0, sm: 570 }, display: "block" }} aria-label="Evolución de ingresos y egresos">
          {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
            const lineY = top + chartHeight * ratio;
            return <line key={ratio} x1={left} y1={lineY} x2={width - right} y2={lineY} stroke="rgba(25,36,47,0.09)" strokeDasharray="4 5" />;
          })}
          <polyline points={incomePoints} fill="none" stroke={PROFIT} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
          <polyline points={expensePoints} fill="none" stroke={COST} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
          {data.length <= 31 ? data.map((point, index) => (
            <React.Fragment key={point.date || index}>
              <circle cx={x(index)} cy={y(point.income)} r="4" fill="#fff" stroke={PROFIT} strokeWidth="3" />
              <circle cx={x(index)} cy={y(point.expenses)} r="4" fill="#fff" stroke={COST} strokeWidth="3" />
            </React.Fragment>
          )) : null}
          {labelIndexes.map((index) => {
            const rawDate = data[index]?.date;
            const date = rawDate ? new Date(`${rawDate}T12:00:00`) : null;
            const label = date && !Number.isNaN(date.getTime())
              ? new Intl.DateTimeFormat("es-AR", { day: "2-digit", month: "short" }).format(date)
              : "—";
            return <text key={index} x={x(index)} y={height - 15} textAnchor="middle" fontSize="12" fill="rgba(25,36,47,0.58)">{label}</text>;
          })}
        </Box>
      </Box>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1} sx={{ mt: 0.5 }}>
        <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>Escala máxima: {formatMoney(maxValue, currency)}</Typography>
        <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>Cada punto representa el total contabilizado de ese día.</Typography>
      </Stack>
    </Box>
  );
}

function AccountBreakdown({ title, description, rows = [], total = 0, color, emptyText, currency }) {
  const max = Math.max(1, ...rows.map((row) => Math.abs(safeNumber(row.amount))));
  return (
    <ErpPanel sx={{ height: "100%" }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 }, "&:last-child": { pb: { xs: 2, sm: 3 } } }}>
        <Typography variant="h5" sx={{ fontWeight: 950, color: ERP_BRAND.dark, fontSize: { xs: 20, sm: 24 } }}>{title}</Typography>
        <Typography variant="body2" sx={{ color: ERP_BRAND.muted, mt: 0.4 }}>{description}</Typography>
        {!rows.length ? (
          <ErpEmpty title={emptyText} description="Las cuentas aparecerán cuando existan asientos contables dentro del período." />
        ) : (
          <Stack spacing={1.8} sx={{ mt: 2.3 }}>
            {rows.slice(0, 8).map((row) => (
              <Box key={row.code || row.name}>
                <Stack direction="row" justifyContent="space-between" spacing={1.25} alignItems="flex-start">
                  <Box sx={{ minWidth: 0 }}>
                    <Typography noWrap sx={{ fontWeight: 900, color: ERP_BRAND.dark }}>{row.name || row.code}</Typography>
                    <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>{row.code || "Sin código"} · {row.entries || 0} líneas</Typography>
                  </Box>
                  <Typography sx={{ fontWeight: 950, color: ERP_BRAND.dark, flexShrink: 0, fontSize: { xs: 14, sm: 16 }, overflowWrap: "anywhere", textAlign: "right" }}>{formatMoney(row.amount, currency)}</Typography>
                </Stack>
                <Box sx={{ height: 7, borderRadius: 999, bgcolor: "rgba(25,36,47,0.07)", overflow: "hidden", mt: 1 }}>
                  <Box sx={{ width: `${Math.max(3, (Math.abs(safeNumber(row.amount)) / max) * 100)}%`, height: "100%", bgcolor: color, borderRadius: 999 }} />
                </Box>
              </Box>
            ))}
            <Divider />
            <Stack direction="row" justifyContent="space-between" spacing={2}>
              <Typography sx={{ fontWeight: 950 }}>Total</Typography>
              <Typography sx={{ fontWeight: 950, color }}>{formatMoney(total, currency)}</Typography>
            </Stack>
          </Stack>
        )}
      </CardContent>
    </ErpPanel>
  );
}

function MobileAccountCard({ account, currency }) {
  const accountCode = account.accountCode || account.code;
  const accountName = account.accountName || account.name || accountCode;
  const accountType = account.accountType || account.type;
  return (
    <Box sx={{ p: 1.65, borderRadius: 2.8, border: `1px solid ${ERP_BRAND.border}`, bgcolor: "#fff" }}>
      <Stack direction="row" justifyContent="space-between" spacing={1.25} alignItems="flex-start">
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 950, color: ERP_BRAND.dark, lineHeight: 1.2 }}>{accountName}</Typography>
          <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>{accountCode || "Sin código"} · {account.movements || account.entries || 0} movimientos</Typography>
        </Box>
        <Chip size="small" label={ACCOUNT_TYPE_LABELS[accountType] || accountType || "Sin clasificar"} sx={{ flexShrink: 0, maxWidth: 132, "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis" } }} />
      </Stack>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1, mt: 1.35 }}>
        {[
          ["Debe", account.debits],
          ["Haber", account.credits],
          ["Saldo", account.balance ?? account.amount],
        ].map(([label, value], index) => (
          <Box key={label} sx={{ gridColumn: index === 2 ? "1 / -1" : "auto", p: 1.1, borderRadius: 2, bgcolor: index === 2 ? "rgba(25,36,47,0.07)" : ERP_BRAND.soft }}>
            <Typography variant="caption" sx={{ color: ERP_BRAND.muted, fontWeight: 850 }}>{label}</Typography>
            <Typography sx={{ mt: 0.2, color: ERP_BRAND.dark, fontWeight: 950, overflowWrap: "anywhere" }}>{formatMoney(value, currency)}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function MobileMovementCard({ movement, hasFinancialData, currency }) {
  return (
    <Box sx={{ p: 1.65, borderRadius: 2.8, border: `1px solid ${ERP_BRAND.border}`, bgcolor: "#fff" }}>
      <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 950, color: ERP_BRAND.dark }}>{movement.movementNumber || "Sin número"}</Typography>
          <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>{formatDateTime(movement.occurredAt)}</Typography>
        </Box>
        <MovementStatusChip status={movement.status} />
      </Stack>
      <Typography sx={{ mt: 1.1, fontWeight: 850, color: ERP_BRAND.dark }}>{movement.description || movement.party?.name || "Sin descripción"}</Typography>
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.8 }}>
        <Chip size="small" label={MOVEMENT_KIND_LABELS[movement.kind] || movement.kind || "Otro"} />
        {movement.sourceNumber ? <Chip size="small" variant="outlined" label={`Ref. ${movement.sourceNumber}`} /> : null}
      </Stack>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1, mt: 1.35 }}>
        <Box sx={{ p: 1.1, borderRadius: 2, bgcolor: ERP_BRAND.soft }}>
          <Typography variant="caption" sx={{ color: ERP_BRAND.muted, fontWeight: 850 }}>Total</Typography>
          <Typography sx={{ mt: 0.2, fontWeight: 950, overflowWrap: "anywhere" }}>{hasFinancialData ? formatMoney(movement.total, movement.currency || currency) : "Restringido"}</Typography>
        </Box>
        <Box sx={{ p: 1.1, borderRadius: 2, bgcolor: safeNumber(movement.balanceDue) > 0 ? "rgba(240,138,62,0.1)" : ERP_BRAND.soft }}>
          <Typography variant="caption" sx={{ color: ERP_BRAND.muted, fontWeight: 850 }}>Pendiente</Typography>
          <Typography sx={{ mt: 0.2, fontWeight: 950, color: safeNumber(movement.balanceDue) > 0 ? "warning.dark" : ERP_BRAND.dark, overflowWrap: "anywhere" }}>{hasFinancialData ? formatMoney(movement.balanceDue, movement.currency || currency) : "—"}</Typography>
        </Box>
      </Box>
    </Box>
  );
}

export default function ProjectStatsPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { projects, activeProject, setActiveProject, loadProjects } = useProjects();
  const [project, setProject] = React.useState(null);
  const [overview, setOverview] = React.useState(null);
  const [previousOverview, setPreviousOverview] = React.useState(null);
  const [view, setView] = React.useState("general");
  const [range, setRange] = React.useState(() => rangeForPreset("month"));
  const [details, setDetails] = React.useState({ balances: [], movements: [], total: 0, loaded: false });
  const [loadingProject, setLoadingProject] = React.useState(true);
  const [loading, setLoading] = React.useState(true);
  const [loadingDetails, setLoadingDetails] = React.useState(false);
  const [error, setError] = React.useState("");
  const [detailError, setDetailError] = React.useState("");

  const contextProject = React.useMemo(
    () => projects.find((item) => item.slug === projectId || item.id === projectId) || activeProject,
    [projects, projectId, activeProject],
  );
  const resolvedProject = project || contextProject;
  const enabled = isProjectModuleEnabled(resolvedProject, "stats");
  const systemEnabled = isProjectModuleEnabled(resolvedProject, "system");

  React.useEffect(() => {
    if (!projects.length) loadProjects();
  }, [projects.length, loadProjects]);

  React.useEffect(() => {
    let mounted = true;
    setLoadingProject(true);
    projectsApi.get(projectId)
      .then((data) => {
        if (!mounted) return;
        setProject(data.project || null);
        if (data.project) setActiveProject(data.project);
      })
      .catch((err) => {
        if (mounted) setError(err.message || "No se pudo cargar el proyecto");
      })
      .finally(() => {
        if (mounted) setLoadingProject(false);
      });
    return () => { mounted = false; };
  }, [projectId, setActiveProject]);

  const loadStats = React.useCallback(async () => {
    if (!enabled || !systemEnabled) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError("");
      const previousRange = previousRangeFor(range.dateFrom, range.dateTo);
      const [currentResult, previousResult] = await Promise.allSettled([
        erpApi.overview(projectId, { dateFrom: range.dateFrom, dateTo: range.dateTo }),
        previousRange
          ? erpApi.overview(projectId, { dateFrom: previousRange.dateFrom, dateTo: previousRange.dateTo })
          : Promise.resolve({ overview: null }),
      ]);
      if (currentResult.status === "rejected") throw currentResult.reason;
      setOverview(currentResult.value.overview || null);
      setPreviousOverview(previousResult.status === "fulfilled" ? previousResult.value.overview || null : null);
    } catch (err) {
      setError(err.message || "No se pudieron cargar las estadísticas");
    } finally {
      setLoading(false);
    }
  }, [enabled, systemEnabled, projectId, range.dateFrom, range.dateTo]);

  const loadDetails = React.useCallback(async () => {
    if (!enabled || !systemEnabled) return;
    try {
      setLoadingDetails(true);
      setDetailError("");
      const params = { dateFrom: range.dateFrom, dateTo: range.dateTo };
      const [balanceResult, movementResult] = await Promise.allSettled([
        erpApi.ledgerBalances(projectId, params),
        erpApi.listMovements(projectId, { ...params, page: 1, pageSize: 25 }),
      ]);
      const balances = balanceResult.status === "fulfilled" ? balanceResult.value.balances || [] : [];
      const movements = movementResult.status === "fulfilled" ? movementResult.value.movements || [] : [];
      const total = movementResult.status === "fulfilled" ? safeNumber(movementResult.value.total) : movements.length;
      setDetails({ balances, movements, total, loaded: true });
      if (balanceResult.status === "rejected" && movementResult.status === "rejected") {
        setDetailError("No se pudo cargar el análisis detallado con los permisos actuales.");
      } else if (balanceResult.status === "rejected") {
        setDetailError("Los movimientos están disponibles, pero no se pudieron consultar los saldos por cuenta.");
      } else if (movementResult.status === "rejected") {
        setDetailError("Los saldos por cuenta están disponibles, pero no se pudo consultar el historial completo de movimientos.");
      }
    } finally {
      setLoadingDetails(false);
    }
  }, [enabled, systemEnabled, projectId, range.dateFrom, range.dateTo]);

  React.useEffect(() => {
    if (!loadingProject) loadStats();
  }, [loadingProject, loadStats]);

  React.useEffect(() => {
    setDetails((current) => ({ ...current, loaded: false }));
    if (!loadingProject && view === "detail") loadDetails();
  }, [loadingProject, loadDetails, view]);

  const selectPreset = (preset) => {
    if (preset === "custom") setRange((current) => ({ ...current, preset }));
    else setRange(rangeForPreset(preset));
  };

  const refresh = async () => {
    await loadStats();
    if (view === "detail") await loadDetails();
  };

  if (loadingProject) return <Alert severity="info">Cargando estadísticas...</Alert>;
  if (!enabled) {
    return <ModuleDisabledState projectId={projectId} title="Estadísticas" description="Este módulo reúne indicadores operativos y comerciales del proyecto." />;
  }
  if (!systemEnabled) {
    return (
      <Stack spacing={2.5}>
        <Box>
          <Typography variant="h3" sx={{ fontWeight: 950, color: ERP_BRAND.dark }}>Estadísticas</Typography>
          <Typography sx={{ color: ERP_BRAND.muted, mt: 0.5 }}>{resolvedProject?.name || projectId}</Typography>
        </Box>
        <Alert severity="info" sx={{ borderRadius: 3 }}>
          Las estadísticas del negocio estarán disponibles cuando el módulo Sistema esté activo. Desde allí se construyen ingresos, egresos, ganancias, cuentas y movimientos.
        </Alert>
      </Stack>
    );
  }

  const financial = overview?.financialSummary || null;
  const previousFinancial = previousOverview?.financialSummary || null;
  const position = overview?.position || {};
  const controls = overview?.controls || {};
  const sales = overview?.sales || {};
  const purchases = overview?.purchases || {};
  const ledger = overview?.ledger || {};
  const movements = overview?.movements || {};
  const recent = overview?.recentMovements || [];
  const currency = overview?.currency || recent[0]?.currency || "ARS";
  const hasFinancialData = Boolean(financial) || Object.prototype.hasOwnProperty.call(sales, "total") || Object.prototype.hasOwnProperty.call(purchases, "total");
  const income = financial ? safeNumber(financial.revenue) : safeNumber(sales.total);
  const expenses = financial
    ? safeNumber(financial.totalExpenses)
    : movementTotals(movements, ["purchase", "expense", "internal_consumption"]);
  const net = financial ? safeNumber(financial.netProfit) : income - expenses;
  const margin = financial ? safeNumber(financial.netMargin) : income ? (net / income) * 100 : 0;
  const previousMovements = previousOverview?.movements || {};
  const previousIncome = previousFinancial ? safeNumber(previousFinancial.revenue) : safeNumber(previousOverview?.sales?.total);
  const previousExpenses = previousFinancial
    ? safeNumber(previousFinancial.totalExpenses)
    : movementTotals(previousMovements, ["purchase", "expense", "internal_consumption"]);
  const previousNet = previousFinancial ? safeNumber(previousFinancial.netProfit) : previousIncome - previousExpenses;
  const incomeComparison = financial?.comparison?.revenue || (previousOverview ? comparisonMetric(income, previousIncome) : null);
  const expenseComparison = financial?.comparison?.totalExpenses
    || financial?.comparison?.operatingExpenses
    || (previousOverview ? comparisonMetric(expenses, previousExpenses) : null);
  const netComparison = financial?.comparison?.netProfit || (previousOverview ? comparisonMetric(net, previousNet) : null);
  const dailyTrend = overview?.financialTrend || [];
  const previousRange = previousRangeFor(range.dateFrom, range.dateTo);
  const trendData = dailyTrend.length
    ? dailyTrend
    : previousOverview && hasFinancialData
      ? [
          { date: previousRange?.dateTo || range.dateFrom, income: previousIncome, expenses: previousExpenses },
          { date: range.dateTo, income, expenses },
        ]
      : [];
  const pendingIn = Object.prototype.hasOwnProperty.call(position, "receivables") ? safeNumber(position.receivables) : safeNumber(sales.pending);
  const pendingOut = Object.prototype.hasOwnProperty.call(position, "payables") ? safeNumber(position.payables) : safeNumber(purchases.pending);
  const expenseShare = income > 0 ? Math.max(0, (expenses / income) * 100) : expenses > 0 ? 100 : 0;
  const expenseShareVisual = Math.min(100, expenseShare);
  const movementRows = Object.entries(movements)
    .map(([kind, values]) => ({ kind, ...values }))
    .sort((a, b) => safeNumber(b.total) - safeNumber(a.total));
  const maxMovementTotal = Math.max(1, ...movementRows.map((row) => safeNumber(row.total)));
  const fullMovementRows = details.movements.length ? details.movements : recent;
  const accountBalances = details.balances.length ? details.balances : position.balances || [];
  const totalOperations = movementRows.reduce((sum, row) => sum + safeNumber(row.count), 0);
  const businessState = !hasFinancialData
    ? { label: "Información financiera restringida", tone: "info", title: "Podés ver la actividad, pero no los importes", text: "Tu acceso actual no incluye los totales financieros. La vista detallada conserva los datos operativos permitidos." }
    : income === 0 && expenses === 0
      ? { label: "Sin actividad suficiente", tone: "info", title: "Todavía no hay números para evaluar", text: "Registrá ventas, compras y gastos confirmados para que el estado del negocio se calcule automáticamente." }
      : net > 0
        ? { label: "Resultado positivo", tone: "success", title: "El negocio está generando ganancia", text: `Después de cubrir los egresos del período quedaron ${formatMoney(net, currency)}.` }
        : net < 0
          ? { label: "Resultado negativo", tone: "error", title: "Los egresos superan a los ingresos", text: `En este período faltaron ${formatMoney(Math.abs(net), currency)} para cubrir todos los costos y gastos.` }
          : { label: "Punto de equilibrio", tone: "warning", title: "El negocio está empatando", text: "Los ingresos alcanzan para cubrir los egresos, pero todavía no queda ganancia." };

  const insightItems = [];
  if (!hasFinancialData) {
    insightItems.push({ tone: "info", icon: <InfoOutlinedIcon />, title: "Importes protegidos", text: "La cantidad de operaciones puede verse, pero los montos requieren visibilidad financiera completa." });
  } else if (income === 0 && expenses === 0) {
    insightItems.push({ tone: "info", icon: <ShowChartRoundedIcon />, title: "Empezá por registrar operaciones", text: "Sin ventas, compras o gastos confirmados no existe una base real para analizar el negocio." });
  } else if (net >= 0) {
    insightItems.push({ tone: "success", icon: <SavingsRoundedIcon />, title: `Queda ${formatPercent(margin)} de cada ingreso`, text: `Por cada $100 que ingresaron, aproximadamente $${Math.max(0, margin).toLocaleString("es-AR", { maximumFractionDigits: 1 })} quedaron como resultado del negocio.` });
  } else {
    insightItems.push({ tone: "error", icon: <TrendingDownRoundedIcon />, title: "Hace falta reducir egresos o aumentar ingresos", text: `Los egresos representan ${formatPercent(expenseShare)} de los ingresos del período.` });
  }
  if (pendingIn > 0) insightItems.push({ tone: "info", icon: <PaymentsRoundedIcon />, title: `${formatMoney(pendingIn, currency)} pendientes de cobro`, text: "Es dinero ya vendido que todavía no ingresó. Dar seguimiento a estos cobros puede mejorar la caja." });
  if (pendingOut > 0) insightItems.push({ tone: "warning", icon: <ShoppingCartCheckoutRoundedIcon />, title: `${formatMoney(pendingOut, currency)} pendientes de pago`, text: "Son compromisos registrados que todavía deben pagarse. Conviene compararlos con la caja disponible." });
  if (safeNumber(position.cashCoverage) > 0 && safeNumber(position.payables) > 0) {
    insightItems.push({
      tone: safeNumber(position.cashCoverage) >= 100 ? "success" : "warning",
      icon: <AccountBalanceWalletRoundedIcon />,
      title: `La caja cubre ${formatPercent(position.cashCoverage)} de las deudas`,
      text: safeNumber(position.cashCoverage) >= 100 ? "La liquidez registrada alcanza para cubrir las cuentas por pagar." : "La caja registrada todavía no alcanza para cubrir todas las obligaciones pendientes.",
    });
  }
  if (!insightItems.length) insightItems.push({ tone: "info", icon: <AssessmentRoundedIcon />, title: `${totalOperations} operaciones analizadas`, text: "La vista detallada permite revisar qué movimientos y cuentas forman estos resultados." });

  return (
    <Stack spacing={{ xs: 1.8, sm: 2.6 }} sx={{ minWidth: 0 }}>
      {error ? <Alert severity="error" onClose={() => setError("")}>{error}</Alert> : null}

      <Stack direction={{ xs: "column", lg: "row" }} justifyContent="space-between" spacing={{ xs: 1.4, sm: 2.2 }} alignItems={{ xs: "stretch", lg: "flex-start" }}>
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1.2} flexWrap="wrap" useFlexGap>
            <Typography variant="h3" sx={{ fontWeight: 950, color: ERP_BRAND.dark, fontSize: { xs: 27, sm: 38 }, lineHeight: 1.08, letterSpacing: { xs: -0.7, sm: -1.2 } }}>Estadísticas del negocio</Typography>
            <Chip icon={<QueryStatsRoundedIcon />} label="Datos reales del sistema" color="success" size="small" sx={{ fontWeight: 850, maxWidth: "100%" }} />
          </Stack>
          <Typography sx={{ color: ERP_BRAND.muted, mt: 0.55, fontSize: { xs: 14, sm: 16 }, lineHeight: 1.45 }}>{resolvedProject?.name || projectId} · una lectura simple primero, el detalle después</Typography>
        </Box>
        <Button variant="outlined" fullWidth startIcon={<RefreshRoundedIcon />} onClick={refresh} disabled={loading || loadingDetails} sx={{ width: { xs: "100%", lg: "auto" }, alignSelf: { xs: "stretch", lg: "center" }, borderRadius: 2.5, textTransform: "none", fontWeight: 850, minHeight: 44 }}>
          Actualizar números
        </Button>
      </Stack>

      <ErpPanel>
        <CardContent sx={{ p: 0 }}>
          <Tabs
            value={view}
            onChange={(_event, value) => setView(value)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ px: { xs: 0.5, sm: 2 }, borderBottom: `1px solid ${ERP_BRAND.border}`, "& .MuiTab-root": { textTransform: "none", fontWeight: 900, minHeight: { xs: 50, sm: 58 }, minWidth: { xs: 145, sm: 160 }, px: { xs: 1.1, sm: 2 }, fontSize: { xs: 13, sm: 14 } }, "& .MuiTab-iconWrapper": { mr: { xs: 0.65, sm: 1 } } }}
          >
            <Tab value="general" icon={<AssessmentRoundedIcon />} iconPosition="start" label="Vista general" />
            <Tab value="detail" icon={<AccountBalanceRoundedIcon />} iconPosition="start" label="Análisis detallado" />
          </Tabs>
          <Box sx={{ p: { xs: 1.5, sm: 2.2 } }}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.35} alignItems={{ xs: "stretch", md: "center" }}>
              <TextField fullWidth select size="small" label="Período" value={range.preset} onChange={(event) => selectPreset(event.target.value)} sx={{ minWidth: { md: 190 }, width: { md: "auto" } }}>
                <MenuItem value="today">Hoy</MenuItem>
                <MenuItem value="week">Últimos 7 días</MenuItem>
                <MenuItem value="month">Mes actual</MenuItem>
                <MenuItem value="30days">Últimos 30 días</MenuItem>
                <MenuItem value="custom">Personalizado</MenuItem>
              </TextField>
              <TextField fullWidth size="small" type="date" label="Desde" value={range.dateFrom} onChange={(event) => setRange((current) => ({ ...current, preset: "custom", dateFrom: event.target.value }))} InputLabelProps={{ shrink: true }} inputProps={{ max: range.dateTo || undefined }} sx={{ width: { md: 170 } }} />
              <TextField fullWidth size="small" type="date" label="Hasta" value={range.dateTo} onChange={(event) => setRange((current) => ({ ...current, preset: "custom", dateTo: event.target.value }))} InputLabelProps={{ shrink: true }} inputProps={{ min: range.dateFrom || undefined }} sx={{ width: { md: 170 } }} />
              <Typography variant="body2" sx={{ color: ERP_BRAND.muted, ml: { md: "auto" }, maxWidth: 440, p: { xs: 1.2, md: 0 }, borderRadius: 2, bgcolor: { xs: ERP_BRAND.soft, md: "transparent" }, fontSize: { xs: 12.5, sm: 14 }, lineHeight: 1.45 }}>
                Se incluyen operaciones confirmadas dentro del período. Los borradores no modifican los resultados.
              </Typography>
            </Stack>
          </Box>
        </CardContent>
      </ErpPanel>

      {loading ? <ErpLoading label="Calculando el estado del negocio..." /> : view === "general" ? (
        <>
          <Box sx={{ position: "relative", overflow: "hidden", borderRadius: { xs: 3, sm: 4 }, bgcolor: businessState.tone === "error" ? "#5A2024" : businessState.tone === "warning" ? "#5A421D" : businessState.tone === "info" ? ERP_BRAND.dark : "#123D2B", color: "#fff", p: { xs: 2, sm: 2.7, md: 3.5 } }}>
            <Box sx={{ position: "absolute", width: { xs: 230, sm: 330 }, height: { xs: 230, sm: 330 }, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.06)", right: -100, top: -150 }} />
            <Grid container spacing={{ xs: 2, md: 3 }} sx={{ position: "relative" }} alignItems="center">
              <Grid item xs={12} lg={7}>
                <Chip label={businessState.label} sx={{ bgcolor: "rgba(255,255,255,0.13)", color: "#fff", fontWeight: 900 }} />
                <Typography variant="h4" sx={{ fontWeight: 950, mt: 1.35, fontSize: { xs: 24, sm: 30 }, lineHeight: 1.12 }}>{businessState.title}</Typography>
                <Typography sx={{ color: "rgba(255,255,255,0.74)", mt: 0.8, maxWidth: 700, lineHeight: 1.6 }}>{businessState.text}</Typography>
                {hasFinancialData ? (
                  <Typography sx={{ fontSize: { xs: 31, sm: 36, md: 48 }, fontWeight: 950, mt: { xs: 1.6, sm: 2.2 }, letterSpacing: -1.2, lineHeight: 1.05, overflowWrap: "anywhere" }}>{formatMoney(net, currency)}</Typography>
                ) : null}
              </Grid>
              <Grid item xs={12} lg={5}>
                <Box sx={{ p: { xs: 1.55, sm: 2.3 }, borderRadius: 3.2, bgcolor: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.12)" }}>
                  <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1.25 }}>
                    <Box sx={{ minWidth: 0 }}><Typography variant="caption" sx={{ color: "rgba(255,255,255,0.66)", fontWeight: 850 }}>INGRESOS</Typography><Typography sx={{ fontSize: { xs: 17, sm: 21 }, fontWeight: 950, overflowWrap: "anywhere", lineHeight: 1.2 }}>{hasFinancialData ? formatMoney(income, currency) : "Restringido"}</Typography></Box>
                    <Box sx={{ textAlign: "right", minWidth: 0 }}><Typography variant="caption" sx={{ color: "rgba(255,255,255,0.66)", fontWeight: 850 }}>EGRESOS</Typography><Typography sx={{ fontSize: { xs: 17, sm: 21 }, fontWeight: 950, overflowWrap: "anywhere", lineHeight: 1.2 }}>{hasFinancialData ? formatMoney(expenses, currency) : "Restringido"}</Typography></Box>
                  </Box>
                  <LinearProgress variant="determinate" value={expenseShareVisual} sx={{ mt: 2, height: 10, borderRadius: 999, bgcolor: "rgba(255,255,255,0.14)", "& .MuiLinearProgress-bar": { borderRadius: 999, bgcolor: expenseShare <= 85 ? "#64D79B" : expenseShare <= 100 ? "#FFC66D" : "#FF8585" } }} />
                  <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ mt: 1.2 }}>
                    <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)" }}>Egresos sobre ingresos</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 950 }}>{hasFinancialData ? formatPercent(expenseShare) : "—"}</Typography>
                  </Stack>
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} xl={3}>
              <SimpleMetric icon={<TrendingUpRoundedIcon />} label="INGRESOS" value={hasFinancialData ? formatMoney(income, currency) : "Sin acceso"} description="Todo lo que el negocio generó en el período." trend={incomeComparison} tone="green" />
            </Grid>
            <Grid item xs={12} sm={6} xl={3}>
              <SimpleMetric icon={<TrendingDownRoundedIcon />} label="EGRESOS" value={hasFinancialData ? formatMoney(expenses, currency) : "Sin acceso"} description="Costos, compras y gastos que reducen el resultado." trend={expenseComparison} inverseTrend tone="orange" />
            </Grid>
            <Grid item xs={12} sm={6} xl={3}>
              <SimpleMetric icon={net >= 0 ? <SavingsRoundedIcon /> : <TrendingDownRoundedIcon />} label={net >= 0 ? "GANANCIA" : "PÉRDIDA"} value={hasFinancialData ? formatMoney(Math.abs(net), currency) : "Sin acceso"} description={net >= 0 ? "Lo que quedó después de cubrir todos los egresos." : "Lo que faltó para cubrir todos los egresos."} trend={netComparison} tone={net >= 0 ? "blue" : "red"} />
            </Grid>
            <Grid item xs={12} sm={6} xl={3}>
              <SimpleMetric icon={<AssessmentRoundedIcon />} label="MARGEN FINAL" value={hasFinancialData ? formatPercent(margin) : "Sin acceso"} description="Porcentaje de los ingresos que quedó como resultado." tone={margin >= 0 ? "purple" : "red"} />
            </Grid>
          </Grid>

          <Grid container spacing={2.5}>
            <Grid item xs={12} lg={7}>
              <ErpPanel sx={{ height: "100%" }}>
                <CardContent sx={{ p: { xs: 2, sm: 3 }, "&:last-child": { pb: { xs: 2, sm: 3 } } }}>
                  <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1.5} alignItems={{ xs: "flex-start", sm: "center" }}>
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 950, color: ERP_BRAND.dark }}>Tendencia del negocio</Typography>
                      <Typography variant="body2" sx={{ color: ERP_BRAND.muted, mt: 0.4 }}>{dailyTrend.length ? "Evolución diaria de lo que entra y lo que sale." : "Comparación del período actual con el período anterior."}</Typography>
                    </Box>
                    {netComparison ? <TrendChip metric={netComparison} /> : null}
                  </Stack>
                  <Box sx={{ mt: 2.1 }}><TrendChart data={trendData} currency={currency} /></Box>
                </CardContent>
              </ErpPanel>
            </Grid>
            <Grid item xs={12} lg={5}>
              <ErpPanel sx={{ height: "100%" }}>
                <CardContent sx={{ p: { xs: 2, sm: 3 }, "&:last-child": { pb: { xs: 2, sm: 3 } } }}>
                  <Typography variant="h5" sx={{ fontWeight: 950, color: ERP_BRAND.dark, fontSize: { xs: 20, sm: 24 } }}>Lo más importante ahora</Typography>
                  <Typography variant="body2" sx={{ color: ERP_BRAND.muted, mt: 0.4 }}>Señales simples para saber dónde mirar primero.</Typography>
                  <Grid container spacing={1.4} sx={{ mt: 0.7 }}>
                    {insightItems.slice(0, 4).map((item, index) => (
                      <Grid item xs={12} key={`${item.title}-${index}`}>
                        <InsightCard {...item} />
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </ErpPanel>
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} lg={3}><ErpMetricCard icon={<PaymentsRoundedIcon />} label="POR COBRAR" value={hasFinancialData ? formatMoney(pendingIn, currency) : "Sin acceso"} detail="Ventas pendientes de cobro" tone="blue" /></Grid>
            <Grid item xs={12} sm={6} lg={3}><ErpMetricCard icon={<ShoppingCartCheckoutRoundedIcon />} label="POR PAGAR" value={hasFinancialData ? formatMoney(pendingOut, currency) : "Sin acceso"} detail="Compras y compromisos pendientes" tone={pendingOut > 0 ? "orange" : "green"} /></Grid>
            <Grid item xs={12} sm={6} lg={3}><ErpMetricCard icon={<PointOfSaleRoundedIcon />} label="OPERACIONES" value={`${totalOperations}`} detail={`${safeNumber(sales.count)} ventas · ${safeNumber(purchases.count)} compras`} tone="dark" /></Grid>
            <Grid item xs={12} sm={6} lg={3}><ErpMetricCard icon={<AccountBalanceWalletRoundedIcon />} label="CAJAS ABIERTAS" value={overview?.openCashSessions === null || overview?.openCashSessions === undefined ? "—" : `${overview.openCashSessions}`} detail="Sesiones de caja actualmente abiertas" tone="green" /></Grid>
          </Grid>
        </>
      ) : (
        <>
          {detailError ? <Alert severity="warning" onClose={() => setDetailError("")}>{detailError}</Alert> : null}
          {loadingDetails && !details.loaded ? <ErpLoading label="Abriendo cuentas y movimientos..." /> : (
            <>
              <ErpPanel sx={{ bgcolor: ERP_BRAND.soft }}>
                <CardContent sx={{ p: { xs: 2.3, md: 2.8 } }}>
                  <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2} alignItems={{ xs: "stretch", md: "center" }}>
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 950, color: ERP_BRAND.dark }}>Análisis detallado</Typography>
                      <Typography variant="body2" sx={{ color: ERP_BRAND.muted, mt: 0.45 }}>Acá podés comprobar qué cuentas y movimientos forman los totales de la vista general.</Typography>
                    </Box>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                      <Button fullWidth variant="outlined" onClick={() => navigate(`/dashboard/proyectos/${projectId}/sistema/cuentas`)} sx={{ width: { sm: "auto" }, textTransform: "none", fontWeight: 850 }}>Abrir cuentas</Button>
                      <Button fullWidth variant="outlined" onClick={() => navigate(`/dashboard/proyectos/${projectId}/sistema/movimientos`)} sx={{ width: { sm: "auto" }, textTransform: "none", fontWeight: 850 }}>Abrir movimientos</Button>
                      <Button fullWidth variant="contained" onClick={() => navigate(`/dashboard/proyectos/${projectId}/sistema/ledger`)} sx={{ width: { sm: "auto" }, textTransform: "none", fontWeight: 850 }}>Abrir libro</Button>
                    </Stack>
                  </Stack>
                </CardContent>
              </ErpPanel>

              {financial ? (
                <Grid container spacing={2.5}>
                  <Grid item xs={12} lg={6}>
                    <AccountBreakdown title="Cuentas que generan ingresos" description="Detalle contable de dónde provienen los ingresos." rows={financial.incomeAccounts || []} total={financial.revenue} color={PROFIT} emptyText="Sin cuentas de ingreso" currency={currency} />
                  </Grid>
                  <Grid item xs={12} lg={6}>
                    <AccountBreakdown title="Cuentas que generan egresos" description="Detalle de costos y gastos que reducen el resultado." rows={financial.expenseAccounts || []} total={financial.totalExpenses} color={COST} emptyText="Sin cuentas de egreso" currency={currency} />
                  </Grid>
                </Grid>
              ) : null}

              <Grid container spacing={2.5}>
                <Grid item xs={12} lg={7}>
                  <ErpPanel>
                    <CardContent sx={{ p: 0 }}>
                      <Box sx={{ p: { xs: 2, sm: 3 }, pb: { xs: 1.5, sm: 2 } }}>
                        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1.4} alignItems={{ xs: "flex-start", sm: "center" }}>
                          <Box>
                            <Typography variant="h5" sx={{ fontWeight: 950, color: ERP_BRAND.dark }}>Saldos por cuenta</Typography>
                            <Typography variant="body2" sx={{ color: ERP_BRAND.muted, mt: 0.4 }}>Debe, haber y saldo de cada cuenta utilizada en el período.</Typography>
                          </Box>
                          <Chip icon={<AccountBalanceRoundedIcon />} label={`${accountBalances.length} cuentas con actividad`} sx={{ fontWeight: 850 }} />
                        </Stack>
                      </Box>
                      {!accountBalances.length ? (
                        <ErpEmpty title="Sin saldos por cuenta" description="Los saldos aparecerán cuando existan asientos contables dentro del período." />
                      ) : (
                        <>
                        <Stack spacing={1.2} sx={{ display: { xs: "flex", sm: "none" }, px: 1.5, pb: 1.5 }}>
                          {accountBalances.map((account) => (
                            <MobileAccountCard key={account.accountCode || account.code || account.accountName || account.name} account={account} currency={currency} />
                          ))}
                        </Stack>
                        <TableContainer sx={{ display: { xs: "none", sm: "block" }, maxHeight: 520 }}>
                          <Table stickyHeader size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Cuenta</TableCell>
                                <TableCell>Tipo</TableCell>
                                <TableCell align="right">Debe</TableCell>
                                <TableCell align="right">Haber</TableCell>
                                <TableCell align="right">Saldo</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {accountBalances.map((account) => {
                                const accountCode = account.accountCode || account.code;
                                const accountName = account.accountName || account.name || accountCode;
                                const accountType = account.accountType || account.type;
                                return (
                                  <TableRow key={accountCode || accountName} hover>
                                    <TableCell>
                                      <Typography sx={{ fontWeight: 900, color: ERP_BRAND.dark }}>{accountName}</Typography>
                                      <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>{accountCode || "Sin código"} · {account.movements || account.entries || 0} movimientos</Typography>
                                    </TableCell>
                                    <TableCell><Chip size="small" label={ACCOUNT_TYPE_LABELS[accountType] || accountType || "Sin clasificar"} /></TableCell>
                                    <TableCell align="right">{formatMoney(account.debits, currency)}</TableCell>
                                    <TableCell align="right">{formatMoney(account.credits, currency)}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 950 }}>{formatMoney(account.balance ?? account.amount, currency)}</TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        </>
                      )}
                    </CardContent>
                  </ErpPanel>
                </Grid>

                <Grid item xs={12} lg={5}>
                  <ErpPanel sx={{ height: "100%" }}>
                    <CardContent sx={{ p: { xs: 2, sm: 3 }, "&:last-child": { pb: { xs: 2, sm: 3 } } }}>
                      <Typography variant="h5" sx={{ fontWeight: 950, color: ERP_BRAND.dark, fontSize: { xs: 20, sm: 24 } }}>Composición de movimientos</Typography>
                      <Typography variant="body2" sx={{ color: ERP_BRAND.muted, mt: 0.4 }}>Importe y cantidad por tipo de operación.</Typography>
                      {!movementRows.length ? <ErpEmpty title="Sin actividad en el período" description="Probá ampliar las fechas o registrá movimientos confirmados." /> : (
                        <Stack spacing={2} sx={{ mt: 2.5 }}>
                          {movementRows.map((row) => (
                            <Box key={row.kind}>
                              <Stack direction="row" justifyContent="space-between" spacing={1.2} alignItems="flex-start">
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography sx={{ fontWeight: 900 }}>{MOVEMENT_KIND_LABELS[row.kind] || row.kind}</Typography>
                                  <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>{row.count || 0} operaciones · {formatMoney(row.pending, currency)} pendiente</Typography>
                                </Box>
                                <Typography sx={{ fontWeight: 950, textAlign: "right", fontSize: { xs: 14, sm: 16 }, overflowWrap: "anywhere" }}>{hasFinancialData ? formatMoney(row.total, currency) : `${row.count || 0}`}</Typography>
                              </Stack>
                              <LinearProgress variant="determinate" value={Math.max(2, (safeNumber(row.total || row.count) / Math.max(1, hasFinancialData ? maxMovementTotal : Math.max(...movementRows.map((item) => safeNumber(item.count))))) * 100)} sx={{ mt: 1, height: 8, borderRadius: 999 }} />
                            </Box>
                          ))}
                        </Stack>
                      )}
                    </CardContent>
                  </ErpPanel>
                </Grid>
              </Grid>

              <ErpPanel>
                <CardContent sx={{ p: 0 }}>
                  <Box sx={{ p: { xs: 2, sm: 3 }, pb: { xs: 1.5, sm: 2 } }}>
                    <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1.5} alignItems={{ xs: "flex-start", sm: "center" }}>
                      <Box>
                        <Typography variant="h5" sx={{ fontWeight: 950, color: ERP_BRAND.dark }}>Movimientos del período</Typography>
                        <Typography variant="body2" sx={{ color: ERP_BRAND.muted, mt: 0.4 }}>Historial para verificar fecha, tipo, estado, importe y saldo pendiente.</Typography>
                      </Box>
                      <Chip icon={<ReceiptLongRoundedIcon />} label={`${details.loaded ? details.total : fullMovementRows.length} movimientos`} sx={{ fontWeight: 850 }} />
                    </Stack>
                  </Box>
                  {!fullMovementRows.length ? (
                    <ErpEmpty title="Sin movimientos para mostrar" description="No hay operaciones registradas dentro de las fechas elegidas." />
                  ) : (
                    <>
                    <Stack spacing={1.2} sx={{ display: { xs: "flex", sm: "none" }, px: 1.5, pb: 1.5 }}>
                      {fullMovementRows.map((movement, index) => (
                        <MobileMovementCard key={movement.id || movement._id || `${movement.movementNumber}-${index}`} movement={movement} hasFinancialData={hasFinancialData} currency={currency} />
                      ))}
                    </Stack>
                    <TableContainer sx={{ display: { xs: "none", sm: "block" }, maxHeight: 560 }}>
                      <Table stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>Número / fecha</TableCell>
                            <TableCell>Tipo</TableCell>
                            <TableCell>Descripción</TableCell>
                            <TableCell align="right">Total</TableCell>
                            <TableCell align="right">Pendiente</TableCell>
                            <TableCell>Estado</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {fullMovementRows.map((movement, index) => (
                            <TableRow key={movement.id || movement._id || `${movement.movementNumber}-${index}`} hover>
                              <TableCell>
                                <Typography sx={{ fontWeight: 900 }}>{movement.movementNumber || "Sin número"}</Typography>
                                <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>{formatDateTime(movement.occurredAt)}</Typography>
                              </TableCell>
                              <TableCell><Chip size="small" label={MOVEMENT_KIND_LABELS[movement.kind] || movement.kind || "Otro"} /></TableCell>
                              <TableCell sx={{ minWidth: 220, maxWidth: 360 }}>
                                <Typography noWrap sx={{ fontWeight: 750 }}>{movement.description || movement.party?.name || "Sin descripción"}</Typography>
                                {movement.sourceNumber ? <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>Ref. {movement.sourceNumber}</Typography> : null}
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 950 }}>{hasFinancialData ? formatMoney(movement.total, movement.currency || currency) : "Restringido"}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 850, color: safeNumber(movement.balanceDue) > 0 ? "warning.main" : ERP_BRAND.muted }}>{hasFinancialData ? formatMoney(movement.balanceDue, movement.currency || currency) : "—"}</TableCell>
                              <TableCell><MovementStatusChip status={movement.status} /></TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    </>
                  )}
                </CardContent>
              </ErpPanel>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} lg={3}><ErpMetricCard icon={<AccountBalanceRoundedIcon />} label="LÍNEAS CONTABLES" value={`${safeNumber(ledger.entries)}`} detail="Registros del debe y haber" tone="dark" /></Grid>
                <Grid item xs={12} sm={6} lg={3}><ErpMetricCard icon={<BalanceRoundedIcon />} label="CONTROL DEL LIBRO" value={ledger.balanced === false ? "Con diferencia" : safeNumber(ledger.entries) ? "Balanceado" : "Sin asientos"} detail={hasFinancialData && safeNumber(ledger.entries) ? `${formatMoney(ledger.debits, currency)} debe · ${formatMoney(ledger.credits, currency)} haber` : "Control de doble partida"} tone={ledger.balanced === false ? "red" : "green"} /></Grid>
                <Grid item xs={12} sm={6} lg={3}><ErpMetricCard icon={<InfoOutlinedIcon />} label="BORRADORES" value={`${safeNumber(controls.draftMovements)}`} detail="No afectan los resultados hasta confirmar" tone="orange" /></Grid>
                <Grid item xs={12} sm={6} lg={3}><ErpMetricCard icon={<ReceiptLongRoundedIcon />} label="SIN ASIENTO" value={`${safeNumber(controls.confirmedWithoutLedger)}`} detail="Operaciones confirmadas para revisar" tone={safeNumber(controls.confirmedWithoutLedger) ? "red" : "green"} /></Grid>
              </Grid>
            </>
          )}
        </>
      )}
    </Stack>
  );
}
